<template>
  <div :class="outerClass" :style="inlineStyle" :data-test-id="dataTestId">
    <div class="justify-start gap-4 inline-flex" :class="classList">
      <div v-if="showIcon" class="block" :class="imageWrapperClasses">
        <Image :media="fields?.icon ?? icon" :class="imageClasses" />
      </div>
      <div class="grow shrink basis-0 flex-col justify-start items-start inline-flex" :class="contentClasses">
        <h3 v-if="fields?.title?.value ?? title?.value" class="self-stretch text-racq-dark-grey" :class="titleClasses">
          <RichText v-if="isTitleRichText" :field="fields?.title ?? title" />

          <Text v-else :field="fields?.title ?? title" />
        </h3>

        <div
          v-if="fields?.subTitle?.value ?? subTitle?.value"
          class="self-stretch text-racq-med-grey richtext"
          :class="subTitleClasses"
        >
          <RichText :field="fields?.subTitle ?? subTitle" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { Text, RichText, Image, Field, ComponentFields, ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import { ImageField } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';

import { PanelTitleTypes } from '@/enums/panel-title-type.enum';
import { usePageStore } from '@/store';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  bindings?: Record<string, any>;
  icon?: ImageField;
  title: Field<string | undefined>;
  subTitle: Field<string | undefined>;
  isTextBlock?: Field<boolean | undefined>;
  fullWidth?: boolean;
  panelType?: Field<string | undefined>;
  dataTestId?: Field<string | undefined>;
  fields?: ComponentFields;
  rendering?: ComponentRendering;
  isTitleRichText?: boolean;
}>();

const outerClass = `${props.bindings?.outerClass ?? ''} ${props.rendering?.params?.Styles ?? ''}`.trim();
const inlineStyle = props.rendering?.params?.inlineStyle;
const dataTestId = usePageStore().dataTestId(
  props.rendering /** EE Rendering */ ?? { fields: props } /** Page builder */
);

const PANEL_TYPE_CLASS_MAP: Record<
  PanelTitleTypes,
  {
    imageWrapper: string;
    image: string;
    contentWrapper: string;
    title: string;
    subTitle: string;
  }
> = {
  [PanelTitleTypes.Display]: {
    imageWrapper: 'w-[64px] h-[64px]',
    image: 'w-[64px]',
    contentWrapper: props.isTextBlock?.value ? 'gap-3' : 'gap-2',
    title: props.isTextBlock?.value ? 'text-lg-bold' : 'text-xl2-bold',
    subTitle: props.isTextBlock?.value ? 'text-base' : 'text-lg',
  },
  [PanelTitleTypes.Primary]: {
    imageWrapper: 'flex justify-center w-[40px] h-[60px]',
    image: 'w-[40px]',
    contentWrapper: props.isTextBlock?.value ? 'gap-3' : 'gap-2',
    title: props.isTextBlock?.value ? 'text-lg-bold' : 'text-xl-bold',
    subTitle: 'text-base',
  },
  [PanelTitleTypes.Secondary]: {
    imageWrapper: 'w-[40px] h-[60px]',
    image: 'w-[40px]',
    contentWrapper: props.isTextBlock?.value ? 'gap-3' : 'gap-1',
    title: 'text-lg-bold',
    subTitle: 'text-base',
  },
  [PanelTitleTypes.Subheading]: {
    imageWrapper: 'w-[40px] h-[60px]',
    image: 'w-[40px]',
    contentWrapper: props.isTextBlock?.value ? `gap-3` : 'gap-1',
    title: props.isTextBlock?.value ? 'text-lg-bold' : 'text-base-bold uppercase',
    subTitle: props.isTextBlock?.value ? 'text-base' : 'text-m pb-2',
  },
};

const classList = computed(() => {
  let list = [];
  list.push((props.fields?.fullWidth ?? props.fullWidth) ? 'w-full' : 'text-block');
  list.push(normalizedType.value == PanelTitleTypes.Display ? 'flex-col md:flex-row' : '');
  return list;
});

const normalizedType = computed<PanelTitleTypes>(() => {
  const raw = (props.fields?.panelType ?? props.panelType)?.value?.trim() || '';
  switch (raw.toLowerCase()) {
    case 'display':
      return PanelTitleTypes.Display;
    case 'primary':
      return PanelTitleTypes.Primary;
    case 'secondary':
      return PanelTitleTypes.Secondary;
    case 'subheading':
      return PanelTitleTypes.Subheading;
    default:
      return PanelTitleTypes.Primary;
  }
});

const classesByType = computed(() => PANEL_TYPE_CLASS_MAP[normalizedType.value]);
const imageWrapperClasses = computed(() => classesByType.value.imageWrapper);
const imageClasses = computed(() => classesByType.value.image);
const contentClasses = computed(() => classesByType.value.contentWrapper);
const titleClasses = computed(() => classesByType.value.title);
const subTitleClasses = computed(() => classesByType.value.subTitle);

const showIcon = computed(() => {
  return (
    (props.fields?.icon ?? props.icon)?.value?.src &&
    !props.isTextBlock?.value &&
    normalizedType.value != PanelTitleTypes.Subheading
  );
});
</script>
